import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';
import styled from 'styled-components';
import queryString from 'query-string';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './lead-form-partner.scss';

const defaultFormValues = {
  firstName: '',
  lastName: '',
  address: '',
  city: '',
  state: 'California',
  zip: '',
  email: '',
  phone: '',
  existingSolar: undefined,
  notes: '',
};

const LeadFormPartner = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);
  const mode = props.blok.mode || '';

  const onSubmit = async (values) => {
    setSubmitState('');

    try {
      const leadSource = props.blok.lead_source || 'website';
      const utmContent = props.blok.utm_content || 'Partner';
      const leadSourceMedium = props.blok.lead_source_medium || '';

      const parsed = queryString.parse(window.location.search);
      let owner = '';

      if (parsed && parsed.partnerId && leadSource === 'Referral') {
        owner = parsed.partnerId;
      }

      await leads.submitLead({
        ...values,
        leadSource,
        leadSourceAccount: !!parsed ? parsed.partnerId || '' : '',
        leadSourceMedium,
        utmContent,
        owner,
      });

      setSubmitState('success');
      metrics.trackLeadFormSubmit(
        window.location.origin + window.location.pathname,
        'LeadFormPartner'
      );
    } catch (e) {
      Sentry.captureException(e, {
        lead: values,
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['lfp'];
  containerClasses.push('theme-' + props.blok.theme);

  const title = props.blok.title || '';
  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const buttonStyle = props.blok.button_style || 'blue';
  const successMessage =
    props.blok.success_message ||
    'Thank you for submitting your information. A representative will be reaching out to you shortly.';

  let modeDefaults = {
    ...defaultFormValues,
  };

  if (mode === 'self_gen') {
    modeDefaults.isHomeowner = '';
  }

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {submitState !== 'success' && (
          <Formik
            initialValues={defaultFormValues}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape({
              firstName: Yup.string().required('Required'),
              lastName: Yup.string().required('Required'),
              zip: Yup.string().matches(/\d{5}/, 'Enter your ZIP code'),
              phone: Yup.string()
                .required('Enter your phone number')
                .matches(PhoneRegex, 'Enter your phone number'),
              email: Yup.string()
                .required('Enter a valid e-mail address')
                .email('Enter a valid e-mail address'),
            })}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

              return (
                <Form className="input-form">
                  {title && <h2>{title}</h2>}
                  <TrackFormDirty
                    formComponent="LeadFormPartner"
                    isDirty={isDirty}
                    setIsDirty={setIsDirty}
                  />
                  <div className="fields">
                    <div className="field">
                      <Field name="firstName" placeholder="First Name" />
                      <ErrorMessage className="error" component="div" name="firstName" />
                    </div>
                    <div className="field">
                      <Field name="lastName" placeholder="Last Name" />
                      <ErrorMessage className="error" component="div" name="lastName" />
                    </div>
                  </div>
                  <div className="field">
                    <Field name="address" placeholder="Street Address" />
                    <ErrorMessage className="error" component="div" name="address" />
                  </div>
                  <div className="fields">
                    <div className="field">
                      <Field name="city" placeholder="City" />
                      <ErrorMessage className="error" component="div" name="city" />
                    </div>
                    <div className="field">
                      <Field name="state" placeholder="State" />
                      <ErrorMessage className="error" component="div" name="state" />
                    </div>
                  </div>
                  <div className="fields">
                    <div className="field">
                      <Field name="zip" placeholder="Zip Code">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="99999"
                            placeholder="Zip Code"
                            type="text"
                            alwaysShowMask={false}
                          />
                        )}
                      </Field>
                      <ErrorMessage className="error" component="div" name="zip" />
                    </div>
                    <div className="field">
                      <Field name="phone" placeholder="Phone">
                        {({ field }) => (
                          <InputMask
                            {...field}
                            mask="(999) 999-9999"
                            placeholder="Phone"
                            type="text"
                            alwaysShowMask={false}
                          />
                        )}
                      </Field>
                      <ErrorMessage className="error" component="div" name="phone" />
                    </div>
                  </div>
                  <div className="field">
                    <Field name="email" placeholder="Email" />
                    <ErrorMessage className="error" component="div" type="email" name="email" />
                  </div>
                  {mode === 'self_gen' && (
                    <div className="field">
                      <Field name="isHomeowner" as="select" placeholder="Are you a homeowner?">
                        <option value="">Are you a homeowner?</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </Field>
                      <ErrorMessage className="error" component="div" name="isHomeowner" />
                    </div>
                  )}
                  <div className="field">
                    <Field name="existingSolar" as="select" placeholder="Do you have solar panels?">
                      <option value="">Do you have solar panels?</option>
                      <option value={true}>Yes</option>
                      <option value={false}>No</option>
                    </Field>
                    <ErrorMessage className="error" component="div" name="existingSolar" />
                  </div>
                  <div className="field">
                    <Field name="notes" as="textarea" rows="3" placeholder="Notes" />
                  </div>
                  {submitState === 'failed' && <div className="error">{errorMessage}</div>}
                  <button
                    className={`btn-${buttonStyle}`}
                    disabled={formik.isSubmitting}
                    type="submit"
                  >
                    {buttonText}
                  </button>
                    <Disclaimer>By submitting this form, I agree to the{' '}
                    <a href="/privacy-policy">Privacy Policy</a> and consent to be contacted by or
                    on behalf of Swell Energy, including by email, calls, and text messages to any
                    telephone number that I provide, about my interest in Swell Energy products
                    and services. I understand my consent is not required to purchase.</Disclaimer>
                </Form>
              );
            }}
          </Formik>
        )}
        {submitState === 'success' && <div className="success">{successMessage}</div>}
      </div>
    </SbEditable>
  );
};

const Disclaimer = styled.span`
  font-size: 0.7rem;
  font-weight: 600;
  display: block;
`;

export default LeadFormPartner;
